package com.siriusxm.pia.components

import kotlinx.browser.window
import org.w3c.dom.DOMRect
import org.w3c.dom.Element
import kotlin.math.max
import kotlin.math.min

/**
 * Utilities for measuring elements and calculating optimal positioning for portals.
 */

/**
 * Position strategy for portal content
 */
enum class PositionStrategy {
    /** Position below the trigger element, aligned to the left */
    BOTTOM_LEFT,

    /** Position below the trigger element, aligned to the right */
    BOTTOM_RIGHT,

    /** Position below the trigger element, centered */
    BOTTOM_CENTER,

    /** Position above the trigger element, aligned to the left */
    TOP_LEFT,

    /** Position above the trigger element, aligned to the right */
    TOP_RIGHT,

    /** Position above the trigger element, centered */
    TOP_CENTER,

    /** Automatically choose the best position based on available space */
    AUTO
}

/**
 * Calculated position for portal content
 */
data class CalculatedPosition(
    val top: Double,
    val left: Double,
    val strategy: PositionStrategy
)

/**
 * Viewport dimensions and scroll information
 */
data class ViewportInfo(
    val width: Double,
    val height: Double,
    val scrollX: Double,
    val scrollY: Double
)

/**
 * Get current viewport information
 */
fun getViewportInfo(): ViewportInfo {
    return ViewportInfo(
        width = window.innerWidth.toDouble(),
        height = window.innerHeight.toDouble(),
        scrollX = window.scrollX,
        scrollY = window.scrollY
    )
}

/**
 * Get element's bounding rectangle relative to the viewport
 */
fun Element.getBoundingRect(): DOMRect {
    return this.getBoundingClientRect()
}

/**
 * Calculate optimal position for portal content relative to a trigger element
 */
fun calculateOptimalPosition(
    triggerElement: Element,
    contentWidth: Double,
    contentHeight: Double,
    strategy: PositionStrategy = PositionStrategy.AUTO,
    offset: Double = 5.0
): CalculatedPosition {
    val triggerRect = triggerElement.getBoundingRect()
    val viewport = getViewportInfo()

    // Calculate absolute positions considering scroll
    val triggerTop = triggerRect.top + viewport.scrollY
    val triggerLeft = triggerRect.left + viewport.scrollX
    val triggerBottom = triggerTop + triggerRect.height
    val triggerRight = triggerLeft + triggerRect.width

    return when (strategy) {
        PositionStrategy.AUTO -> calculateAutoPosition(
            triggerTop, triggerLeft, triggerBottom, triggerRight,
            triggerRect.width, triggerRect.height,
            contentWidth, contentHeight, viewport, offset
        )

        PositionStrategy.BOTTOM_LEFT -> CalculatedPosition(
            top = triggerBottom + offset,
            left = triggerLeft,
            strategy = PositionStrategy.BOTTOM_LEFT
        )

        PositionStrategy.BOTTOM_RIGHT -> CalculatedPosition(
            top = triggerBottom + offset,
            left = triggerRight - contentWidth,
            strategy = PositionStrategy.BOTTOM_RIGHT
        )

        PositionStrategy.BOTTOM_CENTER -> CalculatedPosition(
            top = triggerBottom + offset,
            left = triggerLeft + (triggerRect.width - contentWidth) / 2,
            strategy = PositionStrategy.BOTTOM_CENTER
        )

        PositionStrategy.TOP_LEFT -> CalculatedPosition(
            top = triggerTop - contentHeight - offset,
            left = triggerLeft,
            strategy = PositionStrategy.TOP_LEFT
        )

        PositionStrategy.TOP_RIGHT -> CalculatedPosition(
            top = triggerTop - contentHeight - offset,
            left = triggerRight - contentWidth,
            strategy = PositionStrategy.TOP_RIGHT
        )

        PositionStrategy.TOP_CENTER -> CalculatedPosition(
            top = triggerTop - contentHeight - offset,
            left = triggerLeft + (triggerRect.width - contentWidth) / 2,
            strategy = PositionStrategy.TOP_CENTER
        )
    }
}

/**
 * Calculate the best position automatically based on available space
 */
private fun calculateAutoPosition(
    triggerTop: Double,
    triggerLeft: Double,
    triggerBottom: Double,
    triggerRight: Double,
    triggerWidth: Double,
    triggerHeight: Double,
    contentWidth: Double,
    contentHeight: Double,
    viewport: ViewportInfo,
    offset: Double
): CalculatedPosition {

    // Available space in each direction
    val spaceBelow = viewport.height + viewport.scrollY - triggerBottom
    val spaceAbove = triggerTop - viewport.scrollY

    // Determine vertical position
    val preferBottom = spaceBelow >= contentHeight + offset
    val preferTop = spaceAbove >= contentHeight + offset

    val useBottom = when {
        preferBottom -> true
        preferTop -> false
        spaceBelow > spaceAbove -> true
        else -> false
    }

    // Determine horizontal alignment
    val leftAlignedLeft = triggerLeft
    val rightAlignedLeft = triggerRight - contentWidth
    val centerAlignedLeft = triggerLeft + (triggerWidth - contentWidth) / 2

    val horizontalStrategy = when {
        // Check if centered fits
        centerAlignedLeft >= viewport.scrollX &&
                centerAlignedLeft + contentWidth <= viewport.width + viewport.scrollX -> "center"

        // Check if left-aligned fits
        leftAlignedLeft + contentWidth <= viewport.width + viewport.scrollX -> "left"

        // Check if right-aligned fits
        rightAlignedLeft >= viewport.scrollX -> "right"

        // Default to left if nothing fits perfectly
        else -> "left"
    }

    val strategy = when ("${if (useBottom) "bottom" else "top"}_${horizontalStrategy}") {
        "bottom_left" -> PositionStrategy.BOTTOM_LEFT
        "bottom_right" -> PositionStrategy.BOTTOM_RIGHT
        "bottom_center" -> PositionStrategy.BOTTOM_CENTER
        "top_left" -> PositionStrategy.TOP_LEFT
        "top_right" -> PositionStrategy.TOP_RIGHT
        "top_center" -> PositionStrategy.TOP_CENTER
        else -> PositionStrategy.BOTTOM_LEFT
    }

    // Calculate the actual position based on the determined strategy
    val top = if (useBottom) triggerBottom + offset else triggerTop - contentHeight - offset
    val left = when (horizontalStrategy) {
        "left" -> leftAlignedLeft
        "right" -> rightAlignedLeft
        "center" -> centerAlignedLeft
        else -> leftAlignedLeft
    }

    return CalculatedPosition(top, left, strategy)
}

/**
 * Constrain position to viewport bounds
 */
fun constrainToViewport(position: CalculatedPosition, contentWidth: Double, contentHeight: Double): CalculatedPosition {
    val viewport = getViewportInfo()

    val constrainedLeft = max(
        viewport.scrollX + 5.0, // 5px margin from left edge
        min(position.left, viewport.width + viewport.scrollX - contentWidth - 5.0) // 5px margin from right edge
    )

    val constrainedTop = max(
        viewport.scrollY + 5.0, // 5px margin from top edge
        min(position.top, viewport.height + viewport.scrollY - contentHeight - 5.0) // 5px margin from bottom edge
    )

    return position.copy(
        top = constrainedTop,
        left = constrainedLeft
    )
}

/**
 * Check if an element is within an overflow-hidden container
 */
fun Element.isInOverflowContainer(): Boolean {
    var parent = this.parentElement
    while (parent != null) {
        val style = window.getComputedStyle(parent)
        if (style.getPropertyValue("overflow") == "hidden" ||
            style.getPropertyValue("overflow-x") == "hidden" ||
            style.getPropertyValue("overflow-y") == "hidden"
        ) {
            return true
        }
        parent = parent.parentElement
    }
    return false
}